import { useEffect, type FunctionComponent } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { updateViewProperties } from '@ardoq/view-settings';
import { combineLatest, map, Observable, shareReplay, tap } from 'rxjs';
import { isQuickExploreSelectionEnabled$ } from 'canvas/isQuickExploreSelectionEnabled';
import { isPresentationMode } from 'env';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { getViewModel$ } from '../viewModel$/viewModel$';
import { enhanceViewPropertiesWithViewModifiers } from '../viewModel$/viewModifiers';
import { createViewpointModeBlocksViewCommands } from '../commands';
import type { BlocksViewProperties } from '../types';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { BlocksView } from './BlocksView';

const VIEW_ID = ViewIds.BLOCKS;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

type BlocksViewWithDataWorkProps = BlocksViewProperties &
  HasDataWorkToken & {
    viewStatusReporter: ViewStatusReporter;
  };

const BlocksViewWithDataWork = ({
  dataWorkToken,
  viewStatusReporter,
  ...blocksViewProperties
}: BlocksViewWithDataWorkProps) => {
  useEffect(() => {
    viewStatusReporter.reportWorkFinished(dataWorkToken);
  }, [viewStatusReporter, dataWorkToken]);

  return <BlocksView {...blocksViewProperties} />;
};

const blocksViewCommands$ = isQuickExploreSelectionEnabled$.pipe(
  map(isQuickExploreSelectionEnabled =>
    createViewpointModeBlocksViewCommands({
      isPresentationMode: isPresentationMode(),
      isQuickExploreSelectionEnabled,
    })
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

const viewStatusReporter = createViewStatusReporter(VIEW_INSTANCE_ID);

const blocksViewProps$: Observable<BlocksViewWithDataWorkProps> = combineLatest(
  [
    getViewModel$({
      viewInstanceId: VIEW_INSTANCE_ID,
      viewSettingsWithChanges$: getViewpointModeViewSettingsWithChanges$(
        VIEW_ID,
        VIEW_INSTANCE_ID
      ),
      viewStatusReporter,
    }),
    blocksViewCommands$,
  ]
).pipe(
  tap(([{ dataWorkToken: _dataWorkToken, ...viewModel }, commands]) =>
    dispatchAction(
      updateViewProperties({
        viewId: VIEW_ID,
        viewProperties: enhanceViewPropertiesWithViewModifiers(
          viewModel,
          commands
        ),
      })
    )
  ),
  map(([viewModel, commands]): BlocksViewWithDataWorkProps => ({
    ...viewModel,
    isPresentationMode: isPresentationMode(),
    hideZoomControls: false,
    commands,
    viewStatusReporter,
  })),
  registerWhileSubscribed(viewStatusReporter)
);

const ConnectedBlocksView: FunctionComponent = connect(
  BlocksViewWithDataWork,
  blocksViewProps$
);

export { ConnectedBlocksView };
